import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase, apiFetch } from "@/lib/supabase";
import { Users, ClipboardList, CalendarDays, ListChecks, AlertCircle, UserPlus, FileText, UploadCloud, Sparkles, DollarSign, ClipboardCheck, Handshake, Plus, ChevronDown, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, EmptyState, formatTime, formatDate } from "@/lib/ui";
import WeekAhead from "@/components/WeekAhead";
import BookingInsights from "@/components/BookingInsights";
import FinalizedDocsWidget from "@/components/FinalizedDocsWidget";

function Kpi({ label, value, icon: Icon, testid, tone = "default", onClick }) {
  const tones = {
    default: "bg-[#FDE9D8] text-[#E8712C]",
    warn: "bg-[#FDDCDC] text-[#B02222]",
    ok: "bg-[#E8F0E4] text-[#2C3A29]",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className="p-5 bg-white rounded-2xl border border-[#E8E2D9] text-left hover:border-[#E8712C]/40 transition-colors">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${tones[tone]}`}>
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="text-3xl font-display font-bold tracking-tight">{value}</div>
      <div className="text-sm text-[#57534E] mt-1">{label}</div>
    </button>
  );
}

function QuickAction({ label, icon: Icon, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-[#E8E2D9] hover:border-[#E8712C]/40 hover:bg-[#FDE9D8]/40 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-[#FDE9D8] flex items-center justify-center flex-shrink-0">
        <Icon size={16} className="text-[#E8712C]" strokeWidth={1.5} />
      </div>
      <span className="text-sm font-medium text-[#1C1917]">{label}</span>
    </button>
  );
}

// Map an activity_log row to a deep-link into the right module, or null when
// nothing meaningful to open (e.g. workspace-level events like login).
function activityTarget(e) {
  const t = e.entity_type;
  const id = e.entity_id;
  if (t === "invoices" && id) return `/app/invoices?id=${id}`;
  if (t === "service_contracts" && id) return `/app/contracts?id=${id}`;
  if (t === "service_requests" && id) return `/app/service-requests?id=${id}`;
  if (t === "client_onboarding" && id) return `/app/onboarding?id=${id}`;
  if (t === "tasks" && id) return `/app/tasks?id=${id}`;
  if (t === "bookings" && id) return `/app/scheduling?id=${id}`;
  if (t === "appointments" && id && e.client_id) return `/app/clients/${e.client_id}?tab=appointments`;
  if (t === "form_templates" && id) return `/app/forms?id=${id}`;
  if (t === "form_assignments" && e.client_id) return `/app/clients/${e.client_id}?tab=forms`;
  if (t === "documents" && e.client_id) return `/app/clients/${e.client_id}?tab=documents`;
  if (t === "notes" && e.client_id) return `/app/clients/${e.client_id}?tab=notes`;
  if (t === "calendar_connections") return `/app/calendar-sync`;
  if (e.client_id) return `/app/clients/${e.client_id}`;
  return null;
}

export default function Dashboard() {
  const { profile, currentOrg, currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ active: 0, appt_today: 0, tasks_open: 0, invoices_outstanding_cents: 0, invoices_outstanding_count: 0 });
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [openSRs, setOpenSRs] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!currentOrgId) return;
    (async () => {
      setLoading(true);
      const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(); dayEnd.setHours(23, 59, 59, 999);
      const todayISOStart = dayStart.toISOString();
      const todayISOEnd = dayEnd.toISOString();
      const todayDate = dayStart.toISOString().slice(0, 10);

      const [active, apptToday, tasksOpen, invOutstanding, appts, tasks, srs, activityLog] = await Promise.all([
        supabase.from("clients").select("id", { count: "exact", head: true }).eq("organization_id", currentOrgId).eq("status", "active"),
        supabase.from("appointments").select("id", { count: "exact", head: true }).eq("organization_id", currentOrgId).gte("starts_at", todayISOStart).lte("starts_at", todayISOEnd),
        supabase.from("tasks").select("id", { count: "exact", head: true }).eq("organization_id", currentOrgId).neq("status", "completed"),
        supabase.from("invoices").select("id,invoice_number,total_cents,currency,status,due_date,clients(first_name,last_name)").eq("organization_id", currentOrgId).in("status", ["sent", "overdue"]).order("due_date", { nullsFirst: false }),
        supabase.from("appointments").select("id,title,appointment_type,starts_at,ends_at,status,client_id,clients(first_name,last_name)").eq("organization_id", currentOrgId).gte("starts_at", todayISOStart).lte("starts_at", todayISOEnd).order("starts_at"),
        supabase.from("tasks").select("id,title,due_date,priority,status,client_id,clients(first_name,last_name)").eq("organization_id", currentOrgId).neq("status", "completed").order("due_date", { ascending: true, nullsFirst: false }).limit(6),
        supabase.from("service_requests").select("id,request_number,title,status,priority,created_at,clients(first_name,last_name)").eq("organization_id", currentOrgId).in("status", ["submitted", "in_review", "assigned", "in_progress"]).order("created_at", { ascending: false }).limit(5),
        supabase.from("activity_log").select("id,action,summary,created_at,entity_type,entity_id,client_id,profiles:actor_user_id(full_name)").eq("organization_id", currentOrgId).order("created_at", { ascending: false }).limit(8),
      ]);

      const outstanding = invOutstanding.data || [];
      const totalOutstandingCents = outstanding.reduce((s, i) => s + (i.total_cents || 0), 0);

      const alertList = [];
      if ((tasks.data || []).some((t) => t.due_date && new Date(t.due_date) < new Date(todayDate))) {
        alertList.push({ label: "Overdue tasks", action: () => navigate("/app/tasks"), tone: "warn" });
      }
      if (outstanding.some((i) => i.status === "overdue")) {
        alertList.push({ label: `${outstanding.filter((i) => i.status === "overdue").length} overdue invoice(s)`, action: () => navigate("/app/invoices"), tone: "warn" });
      }
      if ((srs.data || []).some((s) => s.status === "submitted")) {
        alertList.push({ label: "New service requests awaiting review", action: () => navigate("/app/service-requests"), tone: "info" });
      }

      setStats({
        active: active.count || 0,
        appt_today: apptToday.count || 0,
        tasks_open: tasksOpen.count || 0,
        invoices_outstanding_cents: totalOutstandingCents,
        invoices_outstanding_count: outstanding.length,
      });
      setTodaySchedule(appts.data || []);
      setUpcomingTasks(tasks.data || []);
      setOpenSRs(srs.data || []);
      setOutstandingInvoices(outstanding.slice(0, 5));
      setAlerts(alertList);
      setActivity(activityLog.data || []);
      setLoading(false);
    })();
  }, [currentOrgId]);

  const firstName = profile?.full_name?.split(" ")[0] || "there";
  const quickItems = [
    { label: "New Client", icon: UserPlus, to: "/app/clients?new=1", testid: "qa-new-client" },
    { label: "New Appointment", icon: CalendarDays, to: "/app/calendar?new=1", testid: "qa-new-appt" },
    { label: "New Task", icon: ListChecks, to: "/app/tasks?new=1", testid: "qa-new-task" },
    { label: "New Service Request", icon: ClipboardCheck, to: "/app/service-requests?new=1", testid: "qa-new-sr" },
    { label: "New Contract", icon: Handshake, to: "/app/contracts?new=1", testid: "qa-new-contract" },
    { label: "New Invoice", icon: DollarSign, to: "/app/invoices?new=1", testid: "qa-new-invoice" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm text-[#57534E] mb-1">Welcome back,</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1C1917]">{firstName}</h1>
          {currentOrg?.name && <p className="text-sm text-[#57534E] mt-1">{currentOrg.name}</p>}
        </div>
        <div className="relative" ref={menuRef}>
          <Button data-testid="dash-new-btn" onClick={() => setMenuOpen((v) => !v)}
            className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] px-5 py-6">
            <Plus size={16} className="mr-1.5" /> New <ChevronDown size={14} className="ml-1" />
          </Button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E8E2D9] rounded-2xl shadow-lg z-20 overflow-hidden">
              {quickItems.map(({ label, icon: Icon, to, testid }) => (
                <button key={to} data-testid={testid} onClick={() => { setMenuOpen(false); navigate(to); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#FAF8F5] text-left">
                  <Icon size={15} className="text-[#E8712C]" strokeWidth={1.75} /> {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* KPI cards — 4 executive KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-active" label="Active clients" value={stats.active} icon={Users} tone="ok" onClick={() => navigate("/app/clients")} />
        <Kpi testid="kpi-appt-today" label="Today's appointments" value={stats.appt_today} icon={CalendarDays} onClick={() => navigate("/app/calendar")} />
        <Kpi testid="kpi-tasks-open" label="Open tasks" value={stats.tasks_open} icon={ListChecks} onClick={() => navigate("/app/tasks")} />
        <Kpi testid="kpi-invoices-outstanding" label={`Outstanding · ${stats.invoices_outstanding_count} invoice${stats.invoices_outstanding_count === 1 ? "" : "s"}`} value={`$${(stats.invoices_outstanding_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} icon={DollarSign} tone={stats.invoices_outstanding_cents > 0 ? "warn" : "default"} onClick={() => navigate("/app/invoices")} />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-[#FDE9D8]/50 border border-[#F0C69A] rounded-2xl p-4 flex flex-wrap gap-2" data-testid="dash-alerts">
          <Bell size={16} className="text-[#B85C1F] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
          {alerts.map((a) => (
            <button key={a.label} onClick={a.action} className={`text-xs px-3 py-1 rounded-full font-medium ${a.tone === "warn" ? "bg-[#FDDCDC] text-[#B02222]" : "bg-white text-[#57534E]"} hover:opacity-80`}>
              {a.label} →
            </button>
          ))}
        </div>
      )}

      <WeekAhead />

      <FinalizedDocsWidget />

      <BookingInsights />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's schedule */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E2D9]">
          <div className="px-6 py-4 border-b border-[#E8E2D9] flex items-center justify-between">
            <h3 className="font-display font-semibold text-base tracking-tight">Today's schedule</h3>
            <button onClick={() => navigate("/app/calendar")} className="text-xs text-[#E8712C] hover:underline">View calendar →</button>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-[#57534E]">Loading…</div>
          ) : todaySchedule.length === 0 ? (
            <div className="p-10 text-center">
              <CalendarDays size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No appointments today.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E2D9]">
              {todaySchedule.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(a.client_id ? `/app/clients/${a.client_id}?tab=appointments` : `/app/calendar`)}
                  data-testid={`dash-appt-${a.id}`}
                  className="w-full text-left px-6 py-3 flex items-center gap-4 hover:bg-[#FAF8F5] transition-colors"
                >
                  <div className="w-16 text-sm font-medium text-[#E8712C] flex-shrink-0">{formatTime(a.starts_at)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-[#57534E] truncate">
                      {a.clients ? `${a.clients.first_name} ${a.clients.last_name} · ` : ""}{a.appointment_type}
                    </div>
                  </div>
                  <StatusBadge value={a.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tasks due soon */}
        <div className="bg-white rounded-2xl border border-[#E8E2D9]">
          <div className="px-6 py-4 border-b border-[#E8E2D9] flex items-center justify-between">
            <h3 className="font-display font-semibold text-base tracking-tight">Tasks due soon</h3>
            <button onClick={() => navigate("/app/tasks")} className="text-xs text-[#E8712C] hover:underline">View all →</button>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-[#57534E]">Loading…</div>
          ) : upcomingTasks.length === 0 ? (
            <div className="p-10 text-center">
              <ListChecks size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No open tasks.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E2D9]">
              {upcomingTasks.map((t) => {
                const overdue = t.due_date && new Date(t.due_date) < new Date(new Date().toDateString());
                return (
                  <button
                    key={t.id}
                    onClick={() => navigate(`/app/tasks?id=${t.id}`)}
                    data-testid={`dash-task-${t.id}`}
                    className="w-full text-left px-6 py-3 hover:bg-[#FAF8F5] transition-colors"
                  >
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge value={t.priority} />
                      <span className={`text-xs ${overdue ? "text-[#B02222]" : "text-[#57534E]"}`}>
                        {t.due_date ? (overdue ? "Overdue · " : "Due · ") + formatDate(t.due_date) : "No due date"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service requests */}
        <div className="bg-white rounded-2xl border border-[#E8E2D9]">
          <div className="px-6 py-4 border-b border-[#E8E2D9] flex items-center justify-between">
            <h3 className="font-display font-semibold text-base tracking-tight">Service requests</h3>
            <button onClick={() => navigate("/app/service-requests")} className="text-xs text-[#E8712C] hover:underline">View all →</button>
          </div>
          {openSRs.length === 0 ? (
            <div className="p-10 text-center">
              <ClipboardCheck size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No open requests.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {openSRs.map((sr) => (
                <li key={sr.id}>
                  <button
                    onClick={() => navigate(`/app/service-requests?id=${sr.id}`)}
                    data-testid={`dash-sr-${sr.id}`}
                    className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-[#FAF8F5] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{sr.request_number || "SR"} · {sr.title}</div>
                      <div className="text-xs text-[#57534E] truncate">{sr.clients ? `${sr.clients.first_name} ${sr.clients.last_name}` : "—"}</div>
                    </div>
                    <StatusBadge value={sr.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Billing */}
        <div className="bg-white rounded-2xl border border-[#E8E2D9]">
          <div className="px-6 py-4 border-b border-[#E8E2D9] flex items-center justify-between">
            <h3 className="font-display font-semibold text-base tracking-tight">Billing</h3>
            <button onClick={() => navigate("/app/invoices")} className="text-xs text-[#E8712C] hover:underline">View invoices →</button>
          </div>
          {outstandingInvoices.length === 0 ? (
            <div className="p-10 text-center">
              <DollarSign size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">Nothing outstanding.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {outstandingInvoices.map((i) => (
                <li key={i.id}>
                  <button
                    onClick={() => navigate(`/app/invoices?id=${i.id}`)}
                    data-testid={`dash-inv-${i.id}`}
                    className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-[#FAF8F5] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{i.invoice_number}</div>
                      <div className="text-xs text-[#57534E] truncate">
                        {i.clients ? `${i.clients.first_name} ${i.clients.last_name}` : "—"}
                        {i.due_date ? ` · Due ${formatDate(i.due_date)}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">${((i.total_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <StatusBadge value={i.status} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9]">
        <div className="px-6 py-4 border-b border-[#E8E2D9]">
          <h3 className="font-display font-semibold text-base tracking-tight">Recent client activity</h3>
        </div>
        {activity.length === 0 ? (
          <div className="p-10 text-center">
            <Sparkles size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
            <p className="text-sm text-[#57534E]">Activity in your workspace will show up here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E2D9]">
            {activity.map((e) => {
              const target = activityTarget(e);
              const inner = (
                <>
                  <div className="w-8 h-8 rounded-full bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] text-xs font-bold flex-shrink-0">
                    {(e.profiles?.full_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate"><b className="font-medium">{e.profiles?.full_name || "Someone"}</b> <span className="text-[#57534E]">{e.summary}</span></div>
                    <div className="text-xs text-[#A8A29E]">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                </>
              );
              return (
                <li key={e.id}>
                  {target ? (
                    <button
                      onClick={() => navigate(target)}
                      data-testid={`dash-activity-${e.id}`}
                      className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-[#FAF8F5] transition-colors"
                    >
                      {inner}
                    </button>
                  ) : (
                    <div className="px-6 py-3 flex items-center gap-3">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
